import { buildHealthParameterDependencyMap } from './questionnaire-health-parameters.utils'
import { splitText } from './split-question-text'

import type { QuestionnaireResponse, QuestionType } from '@healthblocks-io/core'
import type { IQuestionnaireWithHealthParameters } from 'index'
import type { QuestionAnswer, RenderableAnswer } from '../types'
import type { QuestionnaireResponseWithIncludes } from '../types/questionnaire.types'

export function buildRenderableAnswers({
  questionnaire,
  questionnaireResponse,
  editable = false,
}: {
  questionnaire: IQuestionnaireWithHealthParameters
  questionnaireResponse: QuestionnaireResponse | QuestionnaireResponseWithIncludes
  editable?: boolean
}): RenderableAnswer[] {
  const { dependentLinkIds } = buildHealthParameterDependencyMap(questionnaire)
  const answers = questionnaireResponse?.answers ?? []
  const result: RenderableAnswer[] = []

  for (const question of questionnaire?.item ?? []) {
    const answer = (answers.find((a) => a.linkId === question.linkId) ??
      answers.find((a) => a.linkId === question.id)) as QuestionAnswer | undefined

    if (!answer) continue

    // Check if answer has any content based on type
    const hasContent = answer.text || answer.value || (answer.files && answer.files.length > 0)
    if (!hasContent) continue

    const linkId = question.linkId ?? question.id
    const isEditable = editable && !dependentLinkIds.has(linkId)

    const [title, description] = normalizeAndSplitText(question.text)

    // --- Skip Observation and Display types ---
    if (question.type === 'Observation') continue
    if (question.type === 'Display') continue

    // --- Map question type to render kind ---
    const kind = mapQuestionTypeToKind(question.type)

    result.push({
      kind,
      linkId,
      title,
      description,
      isEditable,
      question,
      answer,
    })
  }

  return result
}

// ================================ Helpers ================================

function normalizeAndSplitText(text?: string): [string, string] {
  const normalizedText = text?.startsWith('#') ? text : text || ''
  return splitText(normalizedText) as [string, string]
}

function mapQuestionTypeToKind(type: QuestionType): RenderableAnswer['kind'] {
  switch (type) {
    case 'File': {
      return 'File'
    }
    case 'Array': {
      return 'Array'
    }
    case 'MultiSelect': {
      return 'MultiSelect'
    }
    case 'MultiSelectComment': {
      return 'MultiSelectComment'
    }
    case 'QuickReplies': {
      return 'QuickReplies'
    }
    case 'Medication': {
      return 'Medication'
    }
    case 'Allergy': {
      return 'Allergy'
    }
    case 'Date': {
      return 'Date'
    }
    default: {
      return 'Default'
    }
  }
}
